// Services/Messaging/MessageListenerService.js
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  setDoc,
  increment,
  writeBatch,
  getDoc,
  addDoc
} from 'firebase/firestore';
import { db, sendMessage } from '../Firebase/FirebaseIndex';
import notificationService from '../Notifications/NotificationService';
import emailService from '../Notifications/EmailService';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import debug from '../../utils/debug';

class MessageListenerService {
  constructor() {
    this.listeners = new Map();
    this.conversationMetadata = new Map();
    this.messageQueues = new Map();
    this.connectionState = 'disconnected';
    this.currentUser = null;
    this.retryAttempts = 0;
    this.maxRetries = 10;  // Increased from 3
    this.retryDelay = 1000;
    this.idleTimeout = null;
    this.idleThreshold = 5 * 60 * 1000; // 5 minutes
    this.callbacks = new Map();
    this.presenceInterval = null;
    // Removed message cache to prevent missing updates
    this.updateDebounceTimers = new Map(); // Debounce timers for updates
    this.listenerRegistry = new Set(); // Track active listeners
  }

  // Initialize the service with user context
  async initialize(user) {
    if (!user) {
      debug.error('MessageListenerService', 'No user provided for initialization');
      return false;
    }

    this.currentUser = user;
    this.connectionState = 'connecting';
    
    try {
      // Set up conversation metadata listener
      await this.setupMetadataListener();
      
      // Presence system disabled with simplified structure
      // await this.initializePresence();
      
      // Set connection state
      this.connectionState = 'connected';
      debug.log('MessageListenerService', 'Service initialized successfully');
      
      // Monitor connection state
      this.startConnectionMonitoring();
      
      // Reset idle timer
      this.resetIdleTimer();
      
      return true;
    } catch (error) {
      debug.error('MessageListenerService', 'Initialization failed:', error);
      this.handleConnectionError(error);
      return false;
    }
  }

  // Set up listener for conversation metadata
  async setupMetadataListener() {
    if (!this.currentUser) return;

    // Listen to all Messages documents where user is a participant
    if (!this.currentUser?.uid) {
      debug.warn('MessageListenerService', 'No currentUser.uid available for metadata listener');
      return;
    }
    
    // Query for messages where user is either organiser or talent
    const messagesRef = collection(db, 'Messages');
    // We'll filter client-side since Firestore doesn't support OR queries easily
    const metadataQuery = query(messagesRef);

    const unsubscribe = onSnapshot(
      metadataQuery,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const data = change.doc.data();
          const conversationId = change.doc.id; // This is the bookingId
          
          // Filter to only include conversations where user is participant
          if (this.currentUser?.uid && 
              (data.organiserUID === this.currentUser.uid || 
               data.talentUID === this.currentUser.uid)) {
            
            if (change.type === 'added' || change.type === 'modified') {
              const previousData = this.conversationMetadata.get(conversationId);
              const simpleMetadata = {
                unreadCount: data.unreadCount || 0,
                lastMessageTime: data.lastMessageTime
              };
              this.conversationMetadata.set(conversationId, simpleMetadata);
              
              // Check for new messages (simplified)
              if (previousData && simpleMetadata.unreadCount > (previousData.unreadCount || 0)) {
                // TODO: Implement notification for new messages
                // this.notifyNewMessage(conversationId, simpleMetadata);
                debug.log('MessageListenerService', `New message in conversation ${conversationId}`, simpleMetadata);
              }
              
              // Notify callbacks
              this.notifyCallbacks('metadata', { conversationId, data: simpleMetadata });
            } else if (change.type === 'removed') {
              this.conversationMetadata.delete(conversationId);
            }
          }
        });
      },
      (error) => {
        debug.error('MessageListenerService', 'Metadata listener error:', error);
        this.handleConnectionError(error);
      }
    );

    this.listeners.set('metadata', unsubscribe);
  }

  // Listen to messages for a specific conversation
  async listenToConversation(bookingId) {
    if (!bookingId || !this.currentUser) return;

    // Check if already listening
    if (this.listeners.has(`conversation_${bookingId}`)) {
      debug.log('MessageListenerService', `Already listening to ${bookingId}`);
      return;
    }

    // Listen to the single conversation document
    const conversationRef = doc(db, 'Messages', bookingId);

    const unsubscribe = onSnapshot(
      conversationRef,
      async (snapshot) => {
        if (!snapshot.exists()) {
          // No messages yet for this conversation
          debug.log('MessageListenerService', `No conversation document found for ${bookingId}`);
          this.notifyCallbacks('messages', { bookingId, messages: [] });
          return;
        }

        const conversationData = snapshot.data();
        const messages = conversationData.messages || [];
        // Simplified structure - no nested metadata
        const unreadCount = conversationData.unreadCount || 0;
        
        // Removed cache check - always process updates
        debug.log('MessageListenerService', `Fetched ${messages.length} messages for ${bookingId}`);
        
        // Debounce rapid updates
        const debounceKey = `messages_${bookingId}`;
        if (this.updateDebounceTimers.has(debounceKey)) {
          clearTimeout(this.updateDebounceTimers.get(debounceKey));
        }
        
        const debounceTimer = setTimeout(async () => {
          // Track unread and undelivered messages - check if current user is the recipient
          const unreadMessages = messages.filter(message => {
            const isRecipient = this.currentUser?.uid && (message.senderId !== this.currentUser.uid);
            return isRecipient && !message.read;
          });
          
          // Track undelivered messages to mark as delivered
          const undeliveredMessages = messages.filter(message => {
            const isRecipient = this.currentUser?.uid && (message.senderId !== this.currentUser.uid);
            return isRecipient && !message.delivered;
          });
          
          // Mark messages as delivered when received by recipient (async, don't wait)
          if (undeliveredMessages.length > 0) {
            this.markMessagesAsDelivered(bookingId, undeliveredMessages).catch(err => 
              debug.error('MessageListenerService', 'Error marking delivered:', err)
            );
          }
          
          // Auto-mark messages as read if conversation is active (async, don't wait)
          if (this.isConversationActive(bookingId) && unreadMessages.length > 0) {
            this.markMessagesAsRead(bookingId, unreadMessages).catch(err => 
              debug.error('MessageListenerService', 'Error marking read:', err)
            );
          }
          
          this.updateDebounceTimers.delete(debounceKey);
        }, 50); // Reduced from 100ms to 50ms for faster updates
        
        this.updateDebounceTimers.set(debounceKey, debounceTimer);
        
        // Notify callbacks with messages immediately (UI update)
        this.notifyCallbacks('messages', { bookingId, messages });
        
        // Simplified metadata notification
        this.notifyCallbacks('metadata', { 
          conversationId: bookingId, 
          data: { unreadCount } 
        });
        
        // Reset idle timer on activity
        this.resetIdleTimer();
      },
      (error) => {
        debug.error('MessageListenerService', `Conversation listener error for ${bookingId}:`, error);
        this.handleConnectionError(error);
      }
    );

    this.listeners.set(`conversation_${bookingId}`, unsubscribe);
    this.listenerRegistry.add(`conversation_${bookingId}`);
  }

  // Stop listening to a specific conversation
  stopListeningToConversation(bookingId) {
    const key = `conversation_${bookingId}`;
    const unsubscribe = this.listeners.get(key);
    
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(key);
      this.listenerRegistry.delete(key);
      debug.log('MessageListenerService', `Stopped listening to ${bookingId}`);
    }
  }

  // Mark messages as delivered
  async markMessagesAsDelivered(bookingId, messages) {
    if (!bookingId || !messages || messages.length === 0) return;

    try {
      // Get the conversation document
      const conversationRef = doc(db, 'Messages', bookingId);
      const conversationDoc = await getDoc(conversationRef);
      
      if (!conversationDoc.exists()) return;
      
      const conversationData = conversationDoc.data();
      const allMessages = conversationData.messages || [];
      
      // Update the delivered status for the specified messages
      const updatedMessages = allMessages.map(msg => {
        const shouldMarkDelivered = messages.some(m => m.id === msg.id);
        if (shouldMarkDelivered && !msg.delivered) {
          return { ...msg, delivered: true, deliveredAt: new Date() };
        }
        return msg;
      });
      
      // Update the conversation document
      await updateDoc(conversationRef, {
        messages: updatedMessages
      });
      
      debug.log('MessageListenerService', `Marked ${messages.length} messages as delivered`);
    } catch (error) {
      debug.error('MessageListenerService', 'Error marking messages as delivered:', error);
    }
  }

  // Mark messages as read
  async markMessagesAsRead(bookingId, messages) {
    if (!bookingId || !messages || messages.length === 0) return;

    try {
      // Get the conversation document
      const conversationRef = doc(db, 'Messages', bookingId);
      const conversationDoc = await getDoc(conversationRef);
      
      if (!conversationDoc.exists()) return;
      
      const conversationData = conversationDoc.data();
      const allMessages = conversationData.messages || [];
      
      // Update the read status for the specified messages - simplified
      const updatedMessages = allMessages.map(msg => {
        const shouldMarkRead = messages.some(m => m.id === msg.id);
        if (shouldMarkRead && !msg.read) {
          return { ...msg, read: true, delivered: true };
        }
        return msg;
      });
      
      // Update the conversation document with simplified structure
      if (this.currentUser?.uid) {
        await updateDoc(conversationRef, {
          messages: updatedMessages,
          unreadCount: 0  // Reset unread count
        });
      }
      
      debug.log('MessageListenerService', `Marked ${messages.length} messages as read`);
    } catch (error) {
      debug.error('MessageListenerService', 'Error marking messages as read:', error);
    }
  }

  // Send push notification for new messages
  async sendNewMessageNotification(conversationId, metadata) {
    try {
      // Determine recipient based on who sent the last message
      const recipientId = metadata.lastMessage.senderId === metadata.organiserUID 
        ? metadata.talentUID 
        : metadata.organiserUID;
      
      if (recipientId && this.currentUser?.uid && recipientId !== this.currentUser.uid) {
        await notificationService.sendNotification(recipientId, {
          title: 'New Message',
          body: metadata.lastMessage.preview,
          data: {
            type: 'new_message',
            conversationId,
            route: `/inbox/${conversationId}`
          }
        });
      }
    } catch (error) {
      debug.error('MessageListenerService', 'Error sending notification:', error);
    }
  }

  // Send a message with optimistic updates
  async sendMessage(bookingId, messageData) {
    if (!bookingId || !messageData) return null;

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const optimisticMessage = {
      id: tempId,
      ...messageData,
      createdAt: new Date(),
      status: 'sending',
      delivered: false,
      read: false,
      sender: messageData.senderName || 'User'  // Include sender name
    };

    // Add to queue for retry if needed
    this.messageQueues.set(tempId, { bookingId, messageData });

    // Notify callbacks with optimistic update immediately
    this.notifyCallbacks('optimisticMessage', { bookingId, message: optimisticMessage });

    // Send message asynchronously without blocking
    this.sendMessageAsync(tempId, bookingId, messageData);
    
    return tempId; // Return immediately for better UX
  }
  
  async sendMessageAsync(tempId, bookingId, messageData) {
    try {
      // Send the actual message using the updated Firebase function
      const result = await sendMessage({
        ...messageData,
        bookingId
      });

      // Remove from queue
      this.messageQueues.delete(tempId);

      // Notify success
      this.notifyCallbacks('messageSent', { 
        bookingId, 
        tempId, 
        actualId: result.messageId 
      });
    } catch (error) {
      debug.error('MessageListenerService', 'Error sending message:', error);
      
      // Notify failure
      this.notifyCallbacks('messageFailed', { bookingId, tempId });
      
      // Retry logic with exponential backoff
      setTimeout(() => {
        this.retryFailedMessage(tempId);
      }, this.retryDelay);
    }
  }


  // Presence system - DISABLED with simplified structure
  async initializePresence() {
    // Presence tracking removed to simplify document structure
    return;
  }

  async updatePresence(online = true) {
    // Presence tracking removed to simplify document structure
    return;
  }

  // Typing indicators - DISABLED with simplified structure
  async setTypingStatus(bookingId, isTyping) {
    // Typing indicators removed to simplify document structure
    return;
  }

  // Connection management
  handleConnectionError(error) {
    this.connectionState = 'error';
    this.retryAttempts++;

    if (this.retryAttempts <= this.maxRetries) {
      const delay = Math.min(this.retryDelay * Math.pow(2, this.retryAttempts - 1), 30000); // Max 30 seconds
      
      setTimeout(() => {
        debug.log('MessageListenerService', `Retrying connection (attempt ${this.retryAttempts})`);
        this.initialize(this.currentUser);
      }, delay);
    } else {
      debug.error('MessageListenerService', 'Max retry attempts reached');
      this.notifyCallbacks('connectionFailed', { error });
    }
  }
  
  // Monitor connection state
  startConnectionMonitoring() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      if (this.connectionState !== 'connected') {
        debug.log('MessageListenerService', 'Network reconnected, reinitializing...');
        this.initialize(this.currentUser);
      }
    });
    
    window.addEventListener('offline', () => {
      this.connectionState = 'offline';
      this.notifyCallbacks('connectionStateChanged', { state: 'offline' });
    });
  }

  // Idle management
  resetIdleTimer() {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }

    this.idleTimeout = setTimeout(() => {
      this.handleIdle();
    }, this.idleThreshold);
  }

  handleIdle() {
    debug.log('MessageListenerService', 'Entering idle state');
    
    // Stop listening to individual conversations (keep metadata listener)
    this.listeners.forEach((unsubscribe, key) => {
      if (key.startsWith('conversation_')) {
        unsubscribe();
        this.listeners.delete(key);
      }
    });

    this.notifyCallbacks('idle', {});
  }

  // Callback management
  subscribe(event, callback) {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, new Set());
    }
    
    this.callbacks.get(event).add(callback);
    
    return () => {
      this.callbacks.get(event)?.delete(callback);
    };
  }

  notifyCallbacks(event, data) {
    const callbacks = this.callbacks.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  // Helper methods
  isConversationActive(bookingId) {
    // Check if the conversation is currently being viewed
    // This would be set by the UI components
    return this.activeConversation === bookingId;
  }

  setActiveConversation(bookingId) {
    this.activeConversation = bookingId;
  }

  // Retry failed messages
  async retryFailedMessage(tempId) {
    const messageData = this.messageQueues.get(tempId);
    if (!messageData) return;

    // Implement exponential backoff retry
    setTimeout(async () => {
      await this.sendMessage(messageData.bookingId, messageData.messageData);
    }, this.retryDelay * 2);
  }

  // Get unread count across all conversations
  getTotalUnreadCount() {
    if (!this.currentUser?.uid) return 0;
    
    let total = 0;
    this.conversationMetadata.forEach((metadata) => {
      if (metadata?.unreadCount) {
        total += metadata.unreadCount[this.currentUser.uid] || 0;
      }
    });
    return total;
  }

  // Cleanup
  async cleanup() {
    debug.log('MessageListenerService', 'Cleaning up service');
    
    // Clear all listeners
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
    
    // Clear presence interval
    if (this.presenceInterval) {
      clearInterval(this.presenceInterval);
    }
    
    // Clear all debounce timers
    this.updateDebounceTimers.forEach(timer => clearTimeout(timer));
    this.updateDebounceTimers.clear();
    
    // Presence removed with simplified structure
    // await this.updatePresence(false);
    
    // Clear data
    this.conversationMetadata.clear();
    this.messageQueues.clear();
    this.callbacks.clear();
    this.listenerRegistry.clear();
    
    // Reset state
    this.connectionState = 'disconnected';
    this.currentUser = null;
    this.retryAttempts = 0;
  }
}

// Export singleton instance
const messageListenerService = new MessageListenerService();
export default messageListenerService;